import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Anforderungs-Dialog
 * (`StaffingRequirementDetailsComponent`). Pilot der I1-Help-Slot-
 * Vereinheitlichung (Cluster I, Audit 2026-05-24): jeder Dialog bekommt
 * den gleichen `<app-dialog-help-button>` rechts vom Schliessen-X — gleiches
 * Icon, gleiche Position, gleiches Verhalten. Diese Tour ist die zugehoerige
 * Help-Tour fuer den Anforderungs-Dialog.
 *
 * Vier kompakte Steps:
 *  1. Welcome   — Was zeigt dieser Dialog?
 *  2. Skill-Section — Pro Qualifikation ein Bereich
 *  3. Eligibility-Gruppen — Verfuegbar / Mit Einschraenkung / Nicht verfuegbar
 *     (das ist der C1/C7-Fix-Kern: Hard-Block ueberschreibt Soft-Block, jede
 *      Person genau ein Status)
 *  4. Soft-Block-Override — „Trotzdem zuweisen" mit Pflicht-Begruendung
 *
 * Pattern analog zu `AUTO_SUGGEST_HELP_TOUR` und `STAFFING_BEDARFSPLAN_HELP_TOUR`.
 */
export const REQUIREMENT_DETAILS_HELP_TOUR: TourConfig = {
  id: TourFeature.RequirementDetailsHelp,
  version: 1,
  title: 'REQUIREMENT_DETAILS_HELP_TOUR_TITLE',
  description: 'REQUIREMENT_DETAILS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Personalbedarfsplanung navigieren. Der Bedarfs-
  // Dialog hat KEINEN deterministischen Opener-Button — er oeffnet via
  // Klick auf eine Bedarfs-Kachel im Kalender. Phase-1-Loesung: `route`
  // only. User landet auf der richtigen Seite und oeffnet selbst.
  launchContext: {
    // D-12 (E1): Personalbedarf → ▦-Muster-Modus der Planung.
    // ensureLaunchContext navigiert via navigateByUrl → Query-String funktioniert.
    route: '/planner?view=muster',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 60,
  // Klassische Guided Tour (Spotlight + Voice-Over moeglich), nicht
  // Help-Mode-Icon-Overlay. `feature-discovery` matcht TourCoordinator.startTour
  // in den V2-Renderer-Pfad.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'REQUIREMENT_DETAILS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight auf den Wrapper.
          anchor: AnchorId.RequirementDialog,
          title: 'TUTORIAL.REQUIREMENT_DETAILS.WELCOME.TITLE',
          content: 'TUTORIAL.REQUIREMENT_DETAILS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'skill-section',
          anchor: AnchorId.RequirementSkillSection,
          spotlightAnchor: AnchorId.RequirementSkillSection,
          title: 'TUTORIAL.REQUIREMENT_DETAILS.SKILL_SECTION.TITLE',
          content: 'TUTORIAL.REQUIREMENT_DETAILS.SKILL_SECTION.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'eligibility-groups',
          // Spotlight auf die Inline-Mitarbeiter-Liste, die nach dem
          // C1/C7-Fix in drei Sektionen aufgeteilt ist.
          anchor: AnchorId.RequirementEmployeeList,
          spotlightAnchor: AnchorId.RequirementEmployeeList,
          title: 'TUTORIAL.REQUIREMENT_DETAILS.GROUPS.TITLE',
          content: 'TUTORIAL.REQUIREMENT_DETAILS.GROUPS.CONTENT',
          placement: 'left',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'soft-block-override',
          anchor: AnchorId.RequirementEmployeeList,
          spotlightAnchor: AnchorId.RequirementEmployeeList,
          title: 'TUTORIAL.REQUIREMENT_DETAILS.OVERRIDE.TITLE',
          content: 'TUTORIAL.REQUIREMENT_DETAILS.OVERRIDE.CONTENT',
          placement: 'left',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step der Tour: kein Zurueck-Button noetig, nur "Abschliessen".
          showPrevButton: false,
        },
      ],
    },
  ],
};
